/**
 * Turning a pyst JSON response into something a browser can show without dying.
 *
 * A query written to explore the API can return anything: two rows or two hundred
 * thousand. The rules here are about not paying for what is not on screen.
 *
 * - Nothing above `MAX_PARSE_BYTES` is parsed at all. `JSON.parse` on a 50 MB body
 *   allocates the whole tree, and pretty-printing it allocates a second copy; that
 *   is what actually kills a tab. Such a body is sampled as it arrived.
 * - A long `value` array is cut to `PREVIEW_ROWS` before formatting, so the cost of
 *   pretty-printing follows what is shown rather than what was returned.
 * - The formatted text is capped as a last resort, for a response that is one huge
 *   object rather than many rows.
 */

/** Bodies larger than this are never parsed. */
export const MAX_PARSE_BYTES = 4 * 1024 * 1024;
/** Rows kept from a `value` array before formatting. */
export const PREVIEW_ROWS = 200;
/** Longest formatted text handed to the view. */
const MAX_TEXT = 256 * 1024;

export interface DataPreview {
    /** Formatted JSON, ready to display. */
    text: string;
    /** The text was cut, so it is not the whole response. */
    truncated: boolean;
    /** Rows in the response and rows shown, when the response is a row set. */
    rows: { total: number; shown: number } | null;
    /** Anything the reader needs to know about what was done to it. */
    note: string | null;
}

/** The `value` array of an OData response, or a bare array of rows. */
function rowsOf(parsed: unknown): unknown[] | null {
    if (Array.isArray(parsed)) {
        return parsed;
    }
    if (parsed && typeof parsed === 'object') {
        const value = (parsed as { value?: unknown }).value;
        if (Array.isArray(value)) {
            return value;
        }
    }
    return null;
}

/** The same response with its rows cut to `PREVIEW_ROWS`. */
function withFewerRows(parsed: unknown, rows: unknown[]): unknown {
    const kept = rows.slice(0, PREVIEW_ROWS);
    if (Array.isArray(parsed)) {
        return kept;
    }
    return { ...(parsed as Record<string, unknown>), value: kept };
}

export function buildDataPreview(text: string): DataPreview {
    const trimmed = text.trim();
    if (!trimmed) {
        return { text: '', truncated: false, rows: null, note: 'Empty response.' };
    }
    if (text.length > MAX_PARSE_BYTES) {
        return {
            text: text.slice(0, 64 * 1024),
            truncated: true,
            rows: null,
            note: `${formatBytes(text.length)} is too large to format in the browser. Narrow the query with $top(...) or py_top(...); the start is shown as it arrived.`,
        };
    }
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        return {
            text: text.slice(0, MAX_TEXT),
            truncated: text.length > MAX_TEXT,
            rows: null,
            note: 'Not JSON, shown as it arrived.',
        };
    }

    const rows = rowsOf(parsed);
    const total = rows?.length ?? 0;
    const cut = rows !== null && total > PREVIEW_ROWS;
    // Cut before formatting: pretty-printing 200 rows costs what 200 rows cost,
    // whatever the response held.
    const formatted = JSON.stringify(cut ? withFewerRows(parsed, rows!) : parsed, null, 2) ?? '';
    const capped = formatted.length > MAX_TEXT;
    return {
        text: capped ? formatted.slice(0, MAX_TEXT) : formatted,
        truncated: cut || capped,
        rows: rows === null ? null : { total, shown: cut ? PREVIEW_ROWS : total },
        note: cut ? `first ${PREVIEW_ROWS} of ${total} rows` : null,
    };
}

export function formatBytes(bytes: number): string {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)} kB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
